import {
  AtSign,
  BarChart3,
  Filter,
  Forward,
  Inbox,
  MailOpen,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { MessageTrendChart } from '../components/analytics/MessageTrendChart.js';
import { WindowPicker } from '../components/analytics/WindowPicker.js';
import { SenderAvatar } from '../components/SenderAvatar.js';
import { useAnalyticsTimeSeries } from '../lib/analytics.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';
import { useDashboardSummary } from '../lib/dashboard.js';
import { NOTIF_KIND_LABEL } from '../lib/notifications.js';
import { formatRelative } from '../lib/time.js';

export function DashboardPage() {
  const { admin } = useAuth();
  const { data, isLoading } = useDashboardSummary();
  const shortName = admin?.adminEmail.split('@')[0] ?? '';
  const recentMessages = data?.recentMessages ?? [];
  const recentNotifications = data?.recentNotifications ?? [];

  const [msgTab, setMsgTab] = useState<'all' | 'unread' | 'read' | 'forwarded'>('all');
  const filteredMessages = useMemo(() => {
    switch (msgTab) {
      case 'unread':
        return recentMessages.filter((m) => m.readAt == null);
      case 'read':
        return recentMessages.filter((m) => m.readAt != null);
      case 'forwarded':
        return recentMessages.filter((m) => m.forwardedAt != null);
      default:
        return recentMessages;
    }
  }, [recentMessages, msgTab]);
  const unreadCount = recentMessages.filter((m) => m.readAt == null).length;
  const forwardedCount = recentMessages.filter((m) => m.forwardedAt != null).length;

  const [chartDays, setChartDays] = useState<number>(7);
  const chartQ = useAnalyticsTimeSeries('messages', chartDays);
  const chartData: Array<{ day: string; n: number }> = useMemo(() => {
    // 7 天走 dashboard/summary 内嵌 messageDaily（一次请求）；30/90 天走 analytics/timeseries
    if (chartDays === 7 && data?.messageDaily) {
      return data.messageDaily;
    }
    return chartQ.data?.points.map((p) => ({ day: p.day, n: p.n })) ?? [];
  }, [chartDays, data?.messageDaily, chartQ.data]);
  const chartLoading = chartDays === 7 ? isLoading : chartQ.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">
            欢迎回来, {shortName} <span className="ml-1">👋</span>
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            这是你的邮箱别名管理中心，所有邮件尽在掌控。
          </p>
        </div>
        <Link
          to="/app/analytics"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 whitespace-nowrap"
        >
          <BarChart3 className="h-4 w-4" />
          查看详细统计
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 [&>*]:min-w-0 md:grid-cols-3 xl:grid-cols-5">
        <Kpi
          title="别名总数"
          value={data?.kpi.aliasTotal ?? 0}
          delta={data?.kpi.aliasNewThisWeek ?? 0}
          icon={<AtSign className="h-5 w-5" />}
          tone="brand"
          loading={isLoading}
        />
        <Kpi
          title="邮件总数"
          value={data?.kpi.messageTotal ?? 0}
          delta={data?.kpi.messageNewThisWeek ?? 0}
          icon={<Inbox className="h-5 w-5" />}
          tone="brand"
          loading={isLoading}
        />
        <Kpi
          title="未读邮件"
          value={data?.kpi.messageUnread ?? 0}
          delta={data?.kpi.messageUnreadThisWeek ?? 0}
          icon={<MailOpen className="h-5 w-5" />}
          tone="green"
          loading={isLoading}
        />
        <Kpi
          title="已转发邮件"
          value={data?.kpi.messageForwarded ?? 0}
          delta={data?.kpi.messageForwardedThisWeek ?? 0}
          icon={<Forward className="h-5 w-5" />}
          tone="brand"
          loading={isLoading}
        />
        <Kpi
          title="已过期别名"
          value={data?.kpi.aliasExpired ?? 0}
          delta={data?.kpi.aliasExpiredThisWeek ?? 0}
          icon={<Trash2 className="h-5 w-5" />}
          tone="red"
          loading={isLoading}
        />
      </div>

      <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHead title="别名使用情况" more="查看全部" to="/app/aliases" />
          {(data?.recentAliases.length ?? 0) === 0 ? (
            <EmptyRow>暂无别名 — 在「别名管理」创建第一个</EmptyRow>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data?.recentAliases.map((a) => (
                <li key={a.id} className="flex items-center gap-3 py-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800">
                    <AtSign className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {a.localPart}
                      {a.domain ? (
                        <span className="text-slate-400 dark:text-slate-500">@{a.domain}</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-500">{a.source}</div>
                  </div>
                  <MiniSpark values={a.spark7d} />
                  <div className="w-8 text-right text-xs text-slate-500 tabular-nums">
                    {a.messageCount}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="flex flex-col lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold whitespace-nowrap">
              邮件统计 (最近 {chartDays} 天)
            </h2>
            <WindowPicker value={chartDays} onChange={setChartDays} />
          </div>
          {/* flex-1：卡片被同行的「活跃别名」撑高时，图表跟着长满，不留空白 */}
          <div className="flex min-h-0 flex-1 flex-col">
            {chartLoading ? (
              <EmptyRow>加载中…</EmptyRow>
            ) : chartData.length === 0 ? (
              <EmptyRow>暂无邮件数据（邮件入库后自动填充）</EmptyRow>
            ) : (
              <MessageTrendChart data={chartData} />
            )}
          </div>
        </Card>
      </div>

      <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHead title="最近邮件" more="查看全部邮件" to="/app/inbox" />
          <div className="mb-3 flex flex-wrap gap-1 border-b border-slate-100 dark:border-slate-800">
            <MsgTabBtn active={msgTab === 'all'} onClick={() => setMsgTab('all')}>
              全部
              {recentMessages.length > 0 ? <TabCount>{recentMessages.length}</TabCount> : null}
            </MsgTabBtn>
            <MsgTabBtn active={msgTab === 'unread'} onClick={() => setMsgTab('unread')}>
              未读
              {unreadCount > 0 ? <TabCount>{unreadCount}</TabCount> : null}
            </MsgTabBtn>
            <MsgTabBtn active={msgTab === 'read'} onClick={() => setMsgTab('read')}>
              已读
            </MsgTabBtn>
            <MsgTabBtn active={msgTab === 'forwarded'} onClick={() => setMsgTab('forwarded')}>
              已转发
              {forwardedCount > 0 ? <TabCount>{forwardedCount}</TabCount> : null}
            </MsgTabBtn>
          </div>
          {isLoading ? (
            <EmptyRow>加载中…</EmptyRow>
          ) : filteredMessages.length === 0 ? (
            <EmptyRow>
              {msgTab === 'all'
                ? '暂无邮件（Email Worker 入库邮件后这里会显示）'
                : msgTab === 'unread'
                  ? '没有未读邮件'
                  : msgTab === 'read'
                    ? '没有已读邮件'
                    : '没有已转发邮件'}
            </EmptyRow>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredMessages.map((m) => {
                const isUnread = m.readAt == null;
                return (
                  <li key={m.id}>
                    <Link
                      to={`/app/inbox/${String(m.id)}`}
                      className={
                        'flex items-center gap-3 py-2.5 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40 ' +
                        (isUnread ? 'bg-brand-50/30 dark:bg-brand-500/5' : '')
                      }
                    >
                      <SenderAvatar
                        fromAddress={m.fromAddress}
                        fromName={m.fromName}
                        className="h-5 w-5"
                      />
                      <div className="min-w-0 flex-1">
                        <div
                          className={
                            'truncate text-sm ' +
                            (isUnread
                              ? 'font-semibold text-slate-900 dark:text-slate-50'
                              : 'text-slate-700 dark:text-slate-200')
                          }
                        >
                          {m.subject || '(无主题)'}
                        </div>
                        <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                          {m.fromName ?? m.fromAddress} · {m.toAddress}
                        </div>
                      </div>
                      <span className="shrink-0 text-xs text-slate-400 whitespace-nowrap">
                        {formatRelative(m.receivedAt)}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
        <Card>
          <CardHead title="系统通知" />
          {recentNotifications.length === 0 ? (
            <EmptyRow>暂无通知</EmptyRow>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {recentNotifications.map((n) => {
                const isUnread = n.readAt == null;
                return (
                  <li key={n.id}>
                    <Link
                      to={n.linkPath ?? '/app'}
                      className={
                        'flex items-start gap-2 py-2.5 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40 ' +
                        (isUnread ? 'bg-brand-50/30 dark:bg-brand-500/5' : '')
                      }
                    >
                      <span
                        className={
                          'mt-1.5 h-2 w-2 shrink-0 rounded-full ' +
                          (isUnread ? 'bg-brand-500' : 'bg-slate-300 dark:bg-slate-600')
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div
                          className={
                            'truncate text-sm ' +
                            (isUnread
                              ? 'font-semibold text-slate-900 dark:text-slate-50'
                              : 'text-slate-700 dark:text-slate-200')
                          }
                        >
                          {n.title}
                        </div>
                        <div className="truncate text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                          {NOTIF_KIND_LABEL[n.kind]} · {formatRelative(n.createdAt)}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {/* 快捷操作 */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">快捷操作</h2>
        {/* 创建 / 导入别名已常驻顶栏，这里只放顶栏没有的入口。 */}
        <div className="grid grid-cols-2 gap-3">
          <QuickAction
            to="/app/rules"
            icon={<Filter className="h-5 w-5" />}
            label="新建规则"
            tone="slate"
          />
          <QuickAction
            to="/app/rules?tab=forwards"
            icon={<Forward className="h-5 w-5" />}
            label="转发设置"
            tone="slate"
          />
        </div>
      </div>
    </div>
  );
}

// ---- 小组件 ----

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={
        'rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 ' +
        (className ?? '')
      }
    >
      {children}
    </div>
  );
}

function CardHead({ title, more, to }: { title: string; more?: string; to?: string }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="text-sm font-semibold whitespace-nowrap">{title}</h2>
      {more && to ? (
        <Link to={to} className="text-xs text-brand-600 hover:underline whitespace-nowrap">
          {more}
        </Link>
      ) : null}
    </div>
  );
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-200 py-8 text-center text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
      {children}
    </div>
  );
}

function MsgTabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px inline-flex items-center gap-1 border-b-2 px-3 py-1.5 text-xs whitespace-nowrap transition-colors',
        active
          ? 'border-brand-500 text-brand-600 dark:text-brand-300'
          : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100',
      )}
    >
      {children}
    </button>
  );
}

function TabCount({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-slate-100 px-1.5 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
      {children}
    </span>
  );
}

function Kpi({
  title,
  value,
  delta,
  icon,
  tone,
  loading,
}: {
  title: string;
  value: number;
  delta: number;
  icon: React.ReactNode;
  tone: 'brand' | 'green' | 'red';
  loading?: boolean;
}) {
  const toneCls =
    tone === 'green'
      ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300'
      : tone === 'red'
        ? 'bg-red-50 text-red-500 dark:bg-red-500/10 dark:text-red-300'
        : 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-300';
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start justify-between">
        <div className={'flex h-10 w-10 items-center justify-center rounded-xl ' + toneCls}>
          {icon}
        </div>
      </div>
      <div className="mt-3 text-xs text-slate-500 whitespace-nowrap">{title}</div>
      <div className="mt-0.5 text-2xl font-semibold tabular-nums">
        {loading ? '…' : value.toLocaleString('zh-CN')}
      </div>
      <div className="mt-1 text-[11px] text-slate-500 whitespace-nowrap">
        {delta > 0 ? '↑' : delta < 0 ? '↓' : ''} {Math.abs(delta)} 本周新增
      </div>
    </div>
  );
}

function QuickAction({
  to,
  icon,
  label,
  tone,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  tone: 'brand' | 'slate';
}) {
  const cls =
    tone === 'brand'
      ? 'border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300'
      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200';
  return (
    <Link
      to={to}
      className={
        'inline-flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ' +
        cls
      }
    >
      {icon}
      {label}
    </Link>
  );
}

// ---- MiniSpark: 别名行内 迷你 sparkline（14×32，7 天）----
function MiniSpark({ values }: { values: number[] }) {
  if (values.length === 0) {
    return <div className="h-6 w-12 shrink-0" aria-hidden="true" />;
  }
  const w = 48;
  const h = 20;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  const points = values.map((n, i) => `${step * i},${h - (n / max) * h}`).join(' ');
  const areaPts = `0,${h} ${points} ${step * (values.length - 1)},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-5 w-12 shrink-0" aria-hidden="true">
      <polygon points={areaPts} fill="rgba(139,92,246,0.15)" />
      <polyline points={points} fill="none" stroke="#8b5cf6" strokeWidth={1.2} />
    </svg>
  );
}
